// Machine profile → "Manuals & Software" — one document row.
//
// Extracted out of ManualsAndSoftware.tsx when B7-FE2 added the public-visibility
// tick (the parent crossed the 300-line rule). The tick is gone again — see the
// ruling below — but the split stays. This tab is still pre-redesign MUI idiom and
// the pending 4B.5 restyle carries it along.
//
// NO public/anonymous document exposure — owner ruling 2026-08-13 (D14): documents
// are login-only. Do not re-add a visibility tick or a public docs list without a
// new owner ruling. Removed with the tick: the B7-BE5 `setDocumentVisibility`
// call (that endpoint is deleted server-side, so it would 404), the
// `adjustPublicDocumentsCount` fleet-cache patch, and the `nameplateEnabled` prop
// that existed only to render the tick inert while the plate was off.
import React from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Box, MenuItem } from "@mui/material";
import Moment from "react-moment";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { Document, DocumentFile } from "../../models/machineDocumentsModel";
import {
  deleteDocument,
  getDocuments,
  selectDocument
} from "../../customerMachineDocumentsSlice";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import colors from "../../../../shared/themes/colors";
import { formatFileSize } from "../../../../shared/models/models";
import TableActionButton from "../../../../shared/components/tables/TableActionButton";
import DownloadIcon from "../../../../shared/assets/icons/DownloadIcon";
import OverflowTooltip from "../../../../shared/components/OverflowTooltip";
import useTranslation from "../../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";

export interface DocumentItemProps {
  documentFile?: Document;
  documentIndex: number;
  onOpenChange?: () => void;
  machineId: string;
}

const DocumentItem = ({
  documentFile,
  onOpenChange,
  machineId
}: DocumentItemProps) => {
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const { translate: tCommon } = useTranslation("common");
  const { translate } = useTranslation("myMachinery");

  const handleEditDocument = (item: Document) => {
    dispatch(selectDocument(item));
    onOpenChange?.();
  };

  const handleDeleteDocument = async (item: Document) => {
    if (item) {
      await dispatch(deleteDocument(item.id));
      await dispatch(getDocuments(machineId));
    }
  };

  const handleDownload = (file: DocumentFile | null) => {
    if (!file) {
      return;
    }

    const link = document.createElement("a");
    link.href = file.content;
    link.target = "_blank";
    link.click();
    link.remove();
  };

  if (!documentFile) {
    return null;
  }

  return (
    <Box
      className="flex justify-between items-center w-full px-4 py-3"
      sx={{ background: colors.secondary.gray98, borderRadius: "4px" }}
    >
      <Box className="flex gap-6">
        <Box className="text-14-medium" color="#333333">
          {documentFile.name}
        </Box>
        <Box className="text-14-regular" color="#333333">
          <Moment date={documentFile.createdDate} format="DD.MM.YYYY, HH:mm" />
        </Box>
        {documentFile.file && (
          <Box className="flex gap-6">
            <Box sx={{ maxWidth: "300px" }}>
              <OverflowTooltip text={documentFile.file.name} className="text-14-regular" color={colors.secondary.gray40} />
            </Box>
            <Box className="text-14-regular" color="#7A8093">
              {formatFileSize(documentFile.file.size)}
            </Box>
          </Box>
        )}
      </Box>

      <Box className="flex items-center" sx={{ gap: "12px", flexShrink: 0 }}>
        {/* NO public/anonymous document exposure — owner ruling 2026-08-13 (D14):
            documents are login-only. Do not re-add a visibility tick or a public
            docs list without a new owner ruling. */}
        <TableActionButton>
          {documentFile?.file && (
            <MenuItem onClick={() => handleDownload(documentFile.file)}>
              <DownloadIcon width={18} height={18} />
              {tCommon("buttons.download")}
            </MenuItem>
          )}

          <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
            <MenuItem onClick={() => handleEditDocument(documentFile)}>
              <EditOutlinedIcon />
              {tCommon("buttons.edit")}
            </MenuItem>
          </PermissionGuard>

          <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Admin}>
            <MenuItem
              onClick={() =>
                confirm({
                  title: translate("deleteDocumentTitle"),
                  text: translate("deleteDocumentText", { name: documentFile.name }),
                  confirmButton: translate("yesDelete"),
                  closeButton: tCommon("common.no"),
                  severity: "error",
                  onConfirm: () => handleDeleteDocument(documentFile)
                })
              }
            >
              <DeleteOutlinedIcon />
              {tCommon("buttons.delete")}
            </MenuItem>
          </PermissionGuard>
        </TableActionButton>
      </Box>
    </Box>
  );
};

export default DocumentItem;
